/**
 * Macro #22 — owner dashboard sections: PARCEL UTILITY / REFERRAL / NEIGHBORHOOD.
 *
 * Reuses the existing dashboard visual language (glass cards, mono labels). ALL
 * values are backend-authoritative. There is NO monetary language anywhere — no
 * "earn", no "potential reward", no fake income (frozen §11/§12).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  parcelUtilityApi,
  type SelfUtility,
  type NeighborsResponse,
} from "../../lib/parcel-utility-api";
import { useI18n } from "../../i18n";

const TIER_LABEL: Record<string, string> = {
  GRID_ZERO: "Grid Zero",
  VIP_CORE: "VIP Core",
  PREMIUM: "Premium",
  RESERVE: "Reserve",
  STANDARD: "Standard",
};

function Section({ title, testId, children }: { title: string; testId: string; children: React.ReactNode }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur"
    >
      <h2 className="mb-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white/60">{title}</h2>
      {children}
    </section>
  );
}

function Stat({ label, value, testId }: { label: string; value: React.ReactNode; testId: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-center">
      <div className="font-mono text-[10px] uppercase text-white/50">{label}</div>
      <div className="text-xl font-semibold text-white" data-testid={testId}>{value}</div>
    </div>
  );
}

function Flag({ on, children, testId }: { on: boolean; children: React.ReactNode; testId: string }) {
  return (
    <span
      data-testid={testId}
      data-on={on ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${
        on ? "border-teal-300/40 bg-teal-400/10 text-teal-200" : "border-white/10 bg-white/[0.02] text-white/40"
      }`}
    >
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${on ? "bg-teal-300" : "bg-white/25"}`} />
      {children}
    </span>
  );
}

// ── REFERRAL ────────────────────────────────────────────────────────────────
function ReferralCard({ util }: { util: SelfUtility }) {
  const { t } = useI18n();
  const r = util.referral;
  const [copied, setCopied] = useState(false);
  const shareLink = useMemo(() => {
    if (!r.referral_code) return "";
    try {
      return `${window.location.origin}/?ref=${r.referral_code}`;
    } catch {
      return `?ref=${r.referral_code}`;
    }
  }, [r.referral_code]);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }, []);

  return (
    <Section title={t("artist.utility.referral.title")} testId="referral-section">
      {r.referral_code ? (
        <div className="space-y-4">
          <div>
            <div className="mb-1 font-mono text-[10px] uppercase text-white/50">{t("artist.utility.referral.yourCode")}</div>
            <div className="flex flex-wrap items-center gap-2">
              <code data-testid="referral-code" className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono text-sm text-teal-200">
                {r.referral_code}
              </code>
              <button
                type="button"
                data-testid="referral-copy-link"
                onClick={() => void copy(shareLink)}
                className="rounded-full border border-teal-300/40 bg-teal-400/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-teal-200 hover:bg-teal-400/20"
              >
                {t("artist.utility.referral.copyLink")}
              </button>
              {copied && <span data-testid="referral-copied" className="text-xs text-teal-300">{t("artist.utility.referral.copied")}</span>}
            </div>
            <div data-testid="referral-share-link" className="mt-2 break-all font-mono text-[11px] text-white/40">{shareLink}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label={t("artist.utility.referral.qualified")} value={r.qualified_referral_count} testId="referral-qualified-count" />
            <Stat label={t("artist.utility.referral.pending")} value={r.pending_referral_count} testId="referral-pending-count" />
          </div>
          {r.qualified_referral_count === 0 && r.pending_referral_count === 0 && (
            <p data-testid="referral-empty" className="text-xs text-white/45">
              {t("artist.utility.referral.empty")}
            </p>
          )}
        </div>
      ) : (
        <p data-testid="referral-no-code" className="text-sm text-white/60">
          {r.referral_enabled
            ? t("artist.utility.referral.preparing")
            : t("artist.utility.referral.ownToUnlock")}
        </p>
      )}
      {r.my_attribution?.attributed && (
        <div data-testid="referral-my-attribution" className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/60">
          {t("artist.utility.referral.wasReferred")}{" "}
          <span data-testid="referral-my-state" className="font-mono uppercase text-white/80">
            {r.my_attribution.qualification_state}
          </span>
        </div>
      )}
    </Section>
  );
}

// ── PARCEL UTILITY ───────────────────────────────────────────────────────────
function ParcelUtilityCard({ util }: { util: SelfUtility }) {
  const { t } = useI18n();
  return (
    <Section title={t("artist.utility.parcel.title")} testId="parcel-utility-section">
      {!util.owns_land ? (
        <p data-testid="parcel-utility-empty" className="text-sm text-white/60">
          {t("artist.utility.parcel.empty")}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={t("artist.utility.parcel.parcels")} value={util.summary.parcel_count} testId="pu-parcel-count" />
            <Stat label={t("artist.utility.parcel.totalArea")} value={util.summary.total_area} testId="pu-total-area" />
            <Stat label={t("artist.utility.parcel.clusters")} value={util.summary.connected_cluster_count} testId="pu-cluster-count" />
            <Stat label={t("artist.utility.parcel.largestCluster")} value={util.summary.largest_cluster_size} testId="pu-largest-cluster" />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="pu-flags">
            <Flag on={util.flags.has_basic_identity} testId="flag-basic-identity">{t("artist.utility.parcel.flagBasicIdentity")}</Flag>
            <Flag on={util.flags.publishable} testId="flag-publishable">{t("artist.utility.parcel.flagPublishable")}</Flag>
            <Flag on={util.flags.published} testId="flag-published">{t("artist.utility.parcel.flagPublished")}</Flag>
            <Flag on={util.flags.has_edge_neighbors} testId="flag-has-neighbors">{t("artist.utility.parcel.flagHasNeighbors")}</Flag>
          </div>
          <div className="space-y-2" data-testid="pu-parcel-list">
            {util.parcels.map((p) => (
              <div
                key={p.parcel_id}
                data-testid={`pu-parcel-${p.parcel_id}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-white/80">#{p.parcel_id}</span>
                  <span className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] uppercase text-white/50">
                    {TIER_LABEL[p.tier] || p.tier}
                  </span>
                  <span className="text-white/50">{p.geometry.w}×{p.geometry.h} · {p.area} u²</span>
                </div>
                <div className="flex items-center gap-3 font-mono text-[11px] text-white/45">
                  <span data-testid={`pu-parcel-${p.parcel_id}-neighbors`}>{t("artist.utility.parcel.neighbors", { n: p.edge_neighbor_count })}</span>
                  <span data-testid={`pu-parcel-${p.parcel_id}-cluster`}>{t("artist.utility.parcel.cluster", { size: p.connected_cluster_size, area: p.connected_cluster_area })}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

// ── NEIGHBORHOOD ──────────────────────────────────────────────────────────────
function NeighborhoodCard({ util }: { util: SelfUtility }) {
  const { t } = useI18n();
  const [openParcel, setOpenParcel] = useState<number | null>(null);
  const [neighbors, setNeighbors] = useState<NeighborsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const openNeighbors = useCallback(async (pid: number) => {
    if (openParcel === pid) {
      setOpenParcel(null);
      setNeighbors(null);
      return;
    }
    setOpenParcel(pid);
    setNeighbors(null);
    setLoading(true);
    try {
      setNeighbors(await parcelUtilityApi.neighbors(pid));
    } catch {
      setNeighbors(null);
    } finally {
      setLoading(false);
    }
  }, [openParcel]);

  return (
    <Section title={t("artist.utility.neighborhood.title")} testId="neighborhood-section">
      {!util.owns_land ? (
        <p data-testid="neighborhood-empty" className="text-sm text-white/60">{t("artist.utility.neighborhood.empty")}</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Stat label={t("artist.utility.neighborhood.edgeNeighbors")} value={util.summary.edge_neighbor_total} testId="nb-edge-total" />
            <Stat label={t("artist.utility.neighborhood.connectedClusters")} value={util.summary.connected_cluster_count} testId="nb-cluster-count" />
          </div>
          {util.clusters.length > 0 && (
            <div className="space-y-2" data-testid="nb-cluster-list">
              {util.clusters.map((c) => (
                <div key={c.cluster_id} data-testid={`nb-cluster-${c.cluster_id}`} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white/70">
                  <span className="font-mono text-white/80">{t("artist.utility.neighborhood.connectedClusterLabel")}</span> · {t("artist.utility.neighborhood.clusterSummary", { size: c.size, area: c.area })}
                  <span className="ml-2 font-mono text-[11px] text-white/40">[{c.member_parcel_ids.join(", ")}]</span>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {util.parcels.map((p) => (
              <div key={p.parcel_id}>
                <button
                  type="button"
                  data-testid={`nb-open-${p.parcel_id}`}
                  onClick={() => void openNeighbors(p.parcel_id)}
                  className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-left font-mono text-[12px] text-white/70 hover:bg-white/5"
                >
                  {t("artist.utility.neighborhood.openParcel", { id: p.parcel_id, n: p.edge_neighbor_count })} {openParcel === p.parcel_id ? "▾" : "▸"}
                </button>
                {openParcel === p.parcel_id && (
                  <div data-testid={`nb-detail-${p.parcel_id}`} className="mt-1 space-y-1 pl-3">
                    {loading && <div className="text-xs text-white/40">{t("artist.utility.neighborhood.loading")}</div>}
                    {!loading && neighbors && neighbors.edge_neighbors.length === 0 && (
                      <div data-testid={`nb-detail-${p.parcel_id}-empty`} className="text-xs text-white/40">{t("artist.utility.neighborhood.noEdgeNeighbors")}</div>
                    )}
                    {!loading && neighbors && neighbors.edge_neighbors.map((n) => (
                      <div key={n.parcel_id} data-testid={`nb-neighbor-${n.parcel_id}`} className="flex items-center justify-between rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-white/70">
                        <span className="font-mono">#{n.parcel_id} · edge {n.shared_edge_length}</span>
                        <span className="font-mono text-[10px] uppercase text-white/45">
                          {n.relationship === "self"
                            ? t("artist.utility.neighborhood.yours")
                            : n.public_identity
                              ? (n.public_identity.display_name || t("artist.utility.neighborhood.published"))
                              : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

export function OwnerUtilitySections() {
  const { t } = useI18n();
  const [util, setUtil] = useState<SelfUtility | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    parcelUtilityApi.self()
      .then((u) => { if (alive) setUtil(u); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  if (err) return null;
  if (!util) {
    return <div data-testid="owner-utility-loading" className="text-center text-white/40 font-mono text-xs py-4">{t("artist.utility.loading")}</div>;
  }
  return (
    <div data-testid="owner-utility-sections" className="space-y-6">
      <ParcelUtilityCard util={util} />
      <ReferralCard util={util} />
      <NeighborhoodCard util={util} />
    </div>
  );
}
